import { X } from 'lucide-react';
import type { InteractionDirection } from '@vagusx/clients';
import { OUTBOUND_CALL_OUTCOME_LABELS } from '@vagusx/shared';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DateRangePicker } from '@/components/custom/date-range-picker';
import type { DateRangeValue } from '@/lib/date-range';

export interface CallHistoryFilterState {
  direction?: InteractionDirection;
  outcomeCategory?: string;
  /** Which calendar period the list is bounded to. `all_time` = unbounded. */
  dateRange: DateRangeValue;
}

/** An untouched filter bar: every call, no bounds. */
export const NO_CALL_HISTORY_FILTERS: CallHistoryFilterState = {
  dateRange: { preset: 'all_time' },
};

/**
 * Radix Select has no value for "nothing selected" — an empty string is
 * reserved to mean "clear the field", so an "Any" option needs a sentinel
 * that is translated back to `undefined` on the way out.
 */
const ANY = '__any__';

interface CallHistoryFiltersProps {
  value: CallHistoryFilterState;
  onChange: (next: CallHistoryFilterState) => void;
}

export function CallHistoryFilters({
  value,
  onChange,
}: CallHistoryFiltersProps): React.ReactElement {
  const hasAny =
    value.direction !== undefined ||
    value.outcomeCategory !== undefined ||
    value.dateRange.preset !== 'all_time';

  const set = (patch: Partial<CallHistoryFilterState>): void => {
    onChange({ ...value, ...patch });
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select
        value={value.direction ?? ANY}
        onValueChange={(next) =>
          set({ direction: next === ANY ? undefined : (next as InteractionDirection) })
        }
      >
        <SelectTrigger className="h-8 w-[140px] text-xs">
          <SelectValue placeholder="Direction" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY}>Any direction</SelectItem>
          <SelectItem value="outbound">Outbound</SelectItem>
          <SelectItem value="inbound">Inbound</SelectItem>
        </SelectContent>
      </Select>

      <Select
        value={value.outcomeCategory ?? ANY}
        onValueChange={(next) => set({ outcomeCategory: next === ANY ? undefined : next })}
      >
        <SelectTrigger className="h-8 w-[190px] text-xs">
          <SelectValue placeholder="Outcome" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY}>Any outcome</SelectItem>
          {OUTBOUND_CALL_OUTCOME_LABELS.map((outcome) => (
            <SelectItem key={outcome.value} value={outcome.value}>
              {outcome.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <DateRangePicker
        id="call-history-range"
        value={value.dateRange}
        onChange={(dateRange) => set({ dateRange })}
        className="text-xs"
      />

      {hasAny && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 text-xs text-muted-foreground"
          onClick={() => onChange(NO_CALL_HISTORY_FILTERS)}
        >
          <X className="h-3 w-3" />
          Clear
        </Button>
      )}
    </div>
  );
}
